/**
 * Silo API Types - Consolidated TypeScript Interfaces
 * 
 * This file consolidates ALL TypeScript interfaces from scattered files into ONE source of truth.
 * Fixes duplicate type imports and organizes types into clear sections for the modular silo API.
 * 
 * Sources consolidated:
 * - @/lib/types/common
 * - @/lib/types/crm  
 * - @/lib/types/orders
 * - @/lib/types/inventory
 * - @/lib/types/users
 * - @/lib/types/catalog
 * - @/lib/types/account
 */

import { z } from 'zod';

// ============================================================================
// COMMON TYPES & UTILITIES
// ============================================================================

// Standard type aliases
export type UUID = string;
export type ISODateTime = string;
export type DecimalString = string;

// Response types
export interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  page_size: number;
}

export interface ErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
  field_errors?: Record<string, string[]>;
}

export interface AuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

// Request/Response pattern helpers
export interface ListQueryParams {
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
}

export type FilterParams = Record<string, string | number | boolean>;
// Request function type for modular API clients
export type RequestFunction = <T = any>(
  url: string,
  options?: {
    method?: string;
    params?: Record<string, any>;
    body?: string;
    headers?: Record<string, string>;
  }
) => Promise<T>;

// Helper types
export type OptionalId = {
  id?: number;
  uuid?: UUID;
};

export type Timestamps = {
  created_at?: ISODateTime;
  updated_at?: ISODateTime;
} | {
  date_created?: ISODateTime;
  date_updated?: ISODateTime;
};

export type Metadata = Record<string, any> | null;

// ============================================================================
// ENUMS
// ============================================================================

// US States
export enum StateEnum {
  AL = 'AL', AK = 'AK', AZ = 'AZ', AR = 'AR', CA = 'CA', CO = 'CO', CT = 'CT',
  DE = 'DE', FL = 'FL', GA = 'GA', HI = 'HI', ID = 'ID', IL = 'IL', IN = 'IN',
  IA = 'IA', KS = 'KS', KY = 'KY', LA = 'LA', ME = 'ME', MD = 'MD', MA = 'MA',
  MI = 'MI', MN = 'MN', MS = 'MS', MO = 'MO', MT = 'MT', NE = 'NE', NV = 'NV',
  NH = 'NH', NJ = 'NJ', NM = 'NM', NY = 'NY', NC = 'NC', ND = 'ND', OH = 'OH',
  OK = 'OK', OR = 'OR', PA = 'PA', RI = 'RI', SC = 'SC', SD = 'SD', TN = 'TN',
  TX = 'TX', UT = 'UT', VT = 'VT', VA = 'VA', WA = 'WA', WV = 'WV', WI = 'WI', WY = 'WY'
}

// Contact submission status
export enum ContactSubmissionStatusEnum {
  TODO = 'todo',
  DOING = 'doing',
  DONE = 'done'
}

// Order statuses - Customer facing
export enum CustomerOrderStatusEnum {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded'
}

// Order statuses - Admin facing
export enum AdminOrderStatusEnum {
  NEW = 'New',
  CONFIRM_CALL = 'Confirm/Call',
  SEND_TO_FULFILLMENT = 'Send to Fulfillment',
  READY_TO_SHIP = 'Ready to Ship',
  PAYMENT_CONFIRMED = 'Payment Confirmed',
  SHIPPED = 'Shipped',
  CANCELLED = 'Cancelled',
  PENDING = 'Pending',
  APPROVED = 'Approved',
  COMPLETED = 'Completed',
  PAID = 'Paid'
}

// Default order status (customer-facing for backward compatibility)
export type OrderStatus = keyof typeof CustomerOrderStatusEnum | string;

// Shipping enums
export enum ShippingSizeEnum {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  EXTRA_LARGE = 'extra_large'
}

export enum ShippingSpeedEnum {
  STANDARD = 'standard',
  EXPEDITED = 'expedited',
  OVERNIGHT = 'overnight',
  TWO_DAY = 'two_day'
}

// Business enums
export enum BusinessEnum {
  RETAIL = 'retail',
  WHOLESALE = 'wholesale',
  DISTRIBUTOR = 'distributor',
  MANUFACTURER = 'manufacturer'
}

// ============================================================================
// USER TYPES
// ============================================================================

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  portal_access: boolean;
  mobile_number: string | null;
  date_joined: string;
  last_login: string | null;
  groups: number[];
  user_permissions: number[];
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company?: string;
  phone?: string;
  date_joined: string;
  last_login?: string;
  is_active: boolean;
  avatar_url?: string;
  timezone?: string;
  contact?: {
    id: string | number;
  };
}

export interface UserCreateInput {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  groups?: number[];
}

export interface UserUpdateInput {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  groups?: number[];
  password?: string;
  portal_access?: boolean;
  features?: Record<string, boolean>;
  mobile_number?: string;
}

export interface ProfileUpdateInput {
  first_name?: string;
  last_name?: string;
  email?: string;
  secondary_email?: string;
  phone_number?: string;
  phone_ext?: string;
  secondary_phone_number?: string;
  secondary_phone_ext?: string;
  title?: string;
  business_name?: string;
  business_address?: string;
  business_city?: string;
  business_state?: string;
  business_zipcode?: string;
  business_phone?: string;
  business_email?: string;
  timezone?: string;
}

// ============================================================================
// CONTACT & CRM TYPES
// ============================================================================

export interface ContactStatus {
  id: number;
  name: string;
  description?: string | null;
  color?: string;
  order?: number;
  is_active: boolean;
}

export interface ContactSource {
  id: number;
  name: string;
  description?: string | null;
}

export interface ContactPipeline {
  id: number;
  name: string;
  description?: string | null;
  stages?: string[];
  is_active: boolean;
}

export interface ContactBusiness {
  id: number;
  uuid: string;
  name: string;
  description?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  email?: string | null;
  phone_number?: string | null;
  oda_license?: string | null;
  date_created: string;
  primary_contact_id?: number | null;
  website?: string | null;
  industry?: string | null;
  size?: string | null;
  contacts_count?: number;
}

export interface Contact {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  secondary_email?: string | null;
  phone_number?: string | null;
  phone_ext?: string | null;
  secondary_phone_number?: string | null;
  secondary_phone_ext?: string | null;
  title?: string | null;
  company?: string;
  status?: ContactStatus | null;
  business?: ContactBusiness | null;
  source?: ContactSource | null;
  pipeline?: ContactPipeline | null;
  user?: User | null;
  created_at: string;
  updated_at: string;
  date_created: string;
  date_updated: string;
  created_by?: User;
  created_by_id?: number | null;
  user_id?: number | null;
  notes_count?: number;
  orders_count?: number;
  total_spent?: number;
}

export interface ContactNote {
  id: number;
  uuid: string;
  contact: number;
  contact_id: number;
  text: string;
  created_by: User;
  timestamp: string;
  is_internal: boolean;
}

export interface ContactSubmission {
  id: number;
  uuid?: string;
  name: string;
  email: string;
  request: string;
  status: 'todo' | 'doing' | 'done' | 'archived';
  assigned_to?: User;
  responded_at?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
}

// Contact input types
export interface ContactCreateInput {
  first_name: string;
  last_name: string;
  email?: string;
  secondary_email?: string;
  phone_number?: string;
  phone_ext?: string;
  secondary_phone_number?: string;
  secondary_phone_ext?: string;
  title?: string;
  status_id?: number;
  business_id?: number;
  source_id?: number;
  pipeline_id?: number;
}

export interface ContactUpdateInput extends Partial<ContactCreateInput> {}

export interface ContactNoteCreateInput {
  contact_id: number;
  text: string;
}

export interface ContactBusinessCreateInput {
  name: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  email?: string;
  phone_number?: string;
  oda_license?: string;
  primary_contact_id?: number;
}

export interface ContactBusinessUpdateInput extends Partial<ContactBusinessCreateInput> {}

export interface ContactSubmissionUpdateInput {
  status?: 'todo' | 'doing' | 'done';
  metadata?: Record<string, any>;
}

// ============================================================================
// PRODUCT & INVENTORY TYPES  
// ============================================================================

export interface ProductType {
  id: number;
  name: string;
  description?: string | null;
  product_count?: number;
  products?: InventoryItem[];
  config_schema?: Record<string, any> | null;
  default_config?: Record<string, any> | null;
}

export interface ProductTerpene {
  id: number;
  product: number;
  terpene: Terpene;
  percentage: string;
}

export interface Terpene {
  id: number;
  name?: string;
  label: string; // API uses 'label'
  description?: string;
  effects?: string[];
  quantity: number; // API uses 'quantity'
  percentage?: string; // For backward compatibility
}

export interface ProductCannabinoid {
  id: number;
  product: number;
  cannabinoid: Cannabinoid;
  percentage: string;
}

export interface Cannabinoid {
  id: number;
  name: string;
  abbreviation: string;
  description?: string;
  quantity: number;
}

export interface ProductFile {
  id: number;
  file_url: string;
  file_type: string;
  file_name: string;
  uploaded_at: string;
}

export interface InventoryItem {
  id: number;
  name: string;
  description: string;
  price: string; // Decimal field as string
  available_quantity: number;
  product_type: ProductType;
  archived: boolean;
  created_ts: string;
  updated_ts: string;
  created_at: string; // For compatibility
  updated_at: string; // For compatibility
  image_url?: string;
  sku?: string;
  barcode?: string;
  terpenes?: ProductTerpene[];
  cannabinoids?: ProductCannabinoid[];
  files?: ProductFile[];
  // Staff-only fields (only present for admin users)
  cost?: string;
  margin?: number;
  supplier_info?: Record<string, any>;
}

// Product input types
export interface ProductCreateInput {
  name: string;
  description: string;
  price: string;
  available_quantity: number;
  product_type_id: number;
  sku?: string;
  barcode?: string;
  image_url?: string;
  cost?: string;
  supplier_info?: Record<string, any>;
}

export interface ProductUpdateInput extends Partial<ProductCreateInput> {}

// Category types
export interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  image_url?: string | null;
  parent_id?: number | null;
  display_order: number;
  is_active: boolean;
  product_count?: number;
}

// Enhanced product detail (for catalog)
export interface ProductDetail extends InventoryItem {
  category?: Category;
  lab_results?: {
    test_date?: string;
    batch_number?: string;
    potency_results?: Record<string, any>;
    safety_results?: Record<string, any>;
  };
  compliance_info?: {
    license_number?: string;
    testing_facility?: string;
    harvest_date?: string;
  };
}

// ============================================================================
// ORDER TYPES
// ============================================================================

export interface PaymentMethod {
  id: number;
  name: string;
  description?: string;
  process_at_checkout: boolean;
  is_active: boolean;
}

export interface Shipping {
  id: number;
  size: string;
  weight: number;
  speed: string;
  price: string;
  carrier?: string;
}

export interface OrderAddress {
  id: number;
  uuid?: string;
  created_by?: User;
  name: string;
  first_name?: string;
  last_name?: string;
  address?: string;
  address1?: string;
  address2?: string;
  city: string;
  state: string;
  zipcode?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  default?: boolean;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface OrderItem {
  id: number;
  order: number;
  product: InventoryItem;
  quantity: number;
  price: string;
  name: string; // Denormalized product name
  // Calculated
  total_price?: string;
}

export interface Order {
  id: number;
  uuid?: string;
  customer: Contact;
  status: OrderStatus;
  order_address: OrderAddress | null;
  payment_method: PaymentMethod;
  shipping?: Shipping | null;
  order_items: OrderItem[];
  notes?: string | OrderNote[] | null; // Can be string or array of note objects
  po_number?: string | null;
  tracking_number?: string | null;
  invoice_number?: string | null;
  sample: boolean;
  timestamp: string;
  complete_ts?: string | null;
  target_date?: string | null;
  created_by?: User;
  // Calculated fields
  subtotal?: string;
  tax?: string;
  shipping_cost?: string;
  total?: string;
  total_amount?: number; // Pre-parsed float version
  // Admin-only fields
  profit?: string;
  margin?: number;
  internal_notes?: string;
}

// Alias for admin interface compatibility
export interface AdminOrder extends Order {}

// Order input types
export interface OrderCreateInput {
  customer_id: number;
  customer?: number; // Alternative customer ID field
  status?: OrderStatus;
  order_address_id?: number;
  order_address?: number; // Alternative address ID field
  order_address_data?: {
    name: string;
    first_name: string;
    last_name: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postal_code: string;
    phone?: string;
    email: string;
    default?: boolean;
  };
  payment_method_id?: number;
  shipping_id?: number;
  order_items: Array<{
    product_id: number;
    quantity: number;
    price?: string;
  }>;
  notes?: string;
  po_number?: string;
  sample?: boolean;
  target_date?: string;
}

export interface OrderUpdateInput {
  status?: OrderStatus;
  notes?: string;
  tracking_number?: string;
  po_number?: string;
  internal_notes?: string;
  target_date?: string | null;
}

// Order Statistics for Dashboard
export interface OrderStats {
  pending_count: number;
  approved_count: number;
  completed_count: number;
  cancelled_count: number;
  total_revenue: string; // Backend Decimal as string
  this_week_count: number;
  this_month_revenue: string;
}

// Paginated Orders Response
export interface PaginatedOrders {
  count: number;
  next: string | null;
  previous: string | null;
  results: Order[];
}

// Order Invoice
export interface OrderInvoice {
  order_id: number;
  order_number: number;
  invoice_id: string | null;
  invoice_url: string | null;
  synced_at: string | null;
  status: string;
  total: string;
  customer: {
    name: string;
    email: string;
  };
  items: Array<{
    name: string;
    quantity: number;
    price: string;
    total: string;
  }>;
}

// Order Timeline Event
export interface OrderTimelineEvent {
  timestamp: string;
  event_type: 'order_created' | 'status_change' | 'note_added' | 'payment_activity' |
              'invoice_activity' | 'invoice_created' | 'quickbooks_sync' | 'tracking_added' | 'order_completed';
  description: string;
  user: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// ACCOUNT & ADDRESS TYPES
// ============================================================================

export interface Address {
  id: number;
  name: string;           // Changed from label
  first_name: string;
  last_name: string;
  address1: string;       // Changed from address_line_1
  address2?: string;      // Changed from address_line_2
  city: string;
  state: string;
  postal_code: string;
  phone?: string;
  email: string;          // Added
  default: boolean;       // Changed from is_default
  is_archived?: boolean;  // Added by backend to indicate archived status
  created_at: string;
  updated_at: string;
  created_by?: number;    // User who created this
  // Removed: user_id, company, country, is_billing, is_shipping
}

export interface AddressCreateInput {
  name: string;           // Backend expects 'name' not 'label'
  first_name: string;
  last_name: string;
  address1: string;       // Backend expects 'address1' not 'address_line_1'
  address2?: string;      // Backend expects 'address2' not 'address_line_2'
  city: string;
  state: string;
  postal_code: string;
  phone: string;          // Required field according to backend
  email: string;          // Required email field
  default?: boolean;      // Backend expects 'default' not 'is_default'
  // Removed: is_active (backend handles via metadata)
}

export interface AddressUpdateInput extends Partial<AddressCreateInput> {}

export interface NotificationPreferences {
  id: number;
  user_id: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  marketing_emails: boolean;
  order_updates: boolean;
  newsletter: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferencesUpdateInput {
  email_notifications?: boolean;
  sms_notifications?: boolean;
  marketing_emails?: boolean;
  order_updates?: boolean;
  newsletter?: boolean;
}

// ============================================================================
// DASHBOARD & ANALYTICS TYPES
// ============================================================================

export interface DashboardStats {
  products: {
    total_products: number;
    out_of_stock: number;
    low_stock: number;
    total_quantity: number;
  };
  orders: {
    total_orders: number;
    pending_orders: number;
    processing_orders: number;
    completed_orders: number;
    orders_last_30_days: number;
    revenue_last_30_days: number;
  };
  contacts: {
    total_contacts: number;
    total_submissions: number;
    pending_submissions: number;
    overdue_submissions: number;
  };
  users: {
    total_users: number;
    admin_users: number;
    customer_users: number;
  };
  health: {
    alerts: {
      critical: number;
      warning: number;
      info: number;
    };
    status: 'healthy' | 'attention_needed';
  };
  last_updated: string;
}

// Alias for admin interface
export interface AdminDashboardStats extends DashboardStats {}

// Dashboard Summary type for the new getDashboardSummary endpoint
export interface DashboardSummary {
  products: {
    total: number
    active: number
    out_of_stock: number
    low_stock: number
  }
  orders: {
    total: number
    pending: number
    processing: number
    completed: number
    revenue: {
      total: number
      month: number
      today: number
    }
  }
  customers: {
    total: number
    active: number
    new_this_month: number
  }
  inventory: {
    total_value: number
    items_count: number
    categories_count: number
  }
}

// Pagination for admin interface
export interface AdminPaginationParams extends ListQueryParams {
  limit?: number;
}

// ============================================================================
// VALIDATION SCHEMAS (ZOD)
// ============================================================================

// Common validation schemas
export const createPaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    results: z.array(itemSchema),
    count: z.number(),
    next: z.string().nullable(),
    previous: z.string().nullable(),
    total_pages: z.number(),
    current_page: z.number(),
    page_size: z.number(),
  });

export const ErrorResponseSchema = z.object({
  error: z.string().optional(),
  message: z.string().optional(),
  detail: z.string().optional(),
  field_errors: z.record(z.array(z.string())).optional(),
});

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Validation helper functions
export const validateRequest = <T>(schema: z.ZodSchema<T>, data: unknown): T => {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new Error(`Validation failed: ${result.error.message}`);
  }
  return result.data;
};

export const validateResponse = <T>(schema: z.ZodSchema<T>, data: unknown): T => {
  return validateRequest(schema, data);
};

export const validatePaginatedResponse = <T>(
  itemSchema: z.ZodSchema<T>,
  data: unknown
): PaginatedResponse<T> => {
  const schema = createPaginatedSchema(itemSchema);
  return validateRequest(schema, data);
};

// Type guards
export function isPaginatedResponse<T>(obj: any): obj is PaginatedResponse<T> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.count === 'number' &&
    Array.isArray(obj.results) &&
    (obj.next === null || typeof obj.next === 'string') &&
    (obj.previous === null || typeof obj.previous === 'string')
  );
}

// Safe data extraction
export function extractPaginatedData<T>(response: any): T[] {
  if (isPaginatedResponse<T>(response)) {
    return response.results;
  }
  if (Array.isArray(response)) {
    return response;
  }
  console.warn('Unexpected response format, returning empty array');
  return [];
}

// Contact formatting utilities
export const formatContactName = (contact: Contact | null | undefined): string => {
  if (!contact) return 'Unknown Customer';
  // Prefer explicit or computed full names before org/email fallbacks
  const combined = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  if (combined) return combined;
  // Some API payloads include a single-field name
  // @ts-ignore - tolerate optional fields not declared on Contact type
  if ((contact as any).name && String((contact as any).name).trim()) return String((contact as any).name).trim();
  // Also check for display_name
  // @ts-ignore - tolerate optional fields not declared on Contact type
  if ((contact as any).display_name && String((contact as any).display_name).trim()) return String((contact as any).display_name).trim();
  if (contact.company) return contact.company;
  if (contact.business?.name) return contact.business.name;
  if (contact.email) return contact.email;
  return 'Unknown Customer';
};

export const formatContactPhone = (contact: Contact | null | undefined): string => {
  if (!contact || !contact.phone_number) return '';
  const ext = contact.phone_ext ? ` x${contact.phone_ext}` : '';
  return `${contact.phone_number}${ext}`;
};

// Inventory utility functions
export const formatInventoryPrice = (item: InventoryItem): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(item.price_float);
};

export const isInventoryLowStock = (item: InventoryItem, threshold: number = 10): boolean => {
  return item.available_quantity <= threshold;
};

// Contact submission status utilities
export const getContactSubmissionStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    'todo': 'bg-blue-100 text-blue-800 border-blue-200',
    'doing': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'done': 'bg-green-100 text-green-800 border-green-200',
  };
  return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
};

export const getContactSubmissionStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    'todo': 'To Do',
    'doing': 'In Progress', 
    'done': 'Completed',
  };
  return labels[status] || status;
};

// Product transformation utility
export const transformProductDetailResponse = (response: any): ProductDetail => {
  // Add any transformation logic here if needed
  return response as ProductDetail;
};

// ============================================================================
// ORDER MANAGEMENT UI ENHANCEMENT TYPES
// ============================================================================

// QuickBooks integration status
export interface QuickBooksStatus {
  invoice_id?: string;
  invoice_url?: string;
  payment_status?: 'pending' | 'partial' | 'paid';
  last_sync?: string;
}

// Order note type for enhanced notes management
export interface OrderNote {
  id?: string;
  text: string;
  timestamp: string;
  user: string;
  type?: 'general' | 'payment' | 'shipping' | 'customer';
  metadata?: Record<string, any>;
}

// Payment activity tracking
export interface PaymentActivity {
  timestamp: string;
  amount: number;
  method: string;
  reference?: string;
  user?: string;
}

// Financial breakdown for order summary
export interface FinancialBreakdown {
  subtotal: number;
  tax: number;
  shipping: number;
  discounts: number;
  fees: number;
  total: number;
  paid: number;
  remaining: number;
}

// ============================================================================
// ORDER TIMELINE & HISTORY TYPES
// ============================================================================

// Order timeline activity types
export interface OrderTimelineActivity {
  id: string;
  type: 'order_created' | 'status_change' | 'payment' | 'invoice' | 'quickbooks_sync' | 'tracking' | 'note' | 'custom';
  title: string;
  description: string;
  timestamp: string;
  user?: string;
  metadata?: {
    old_value?: any;
    new_value?: any;
    entity_id?: string | number;
    entity_type?: string;
    [key: string]: any;
  };
  icon?: string;
  color?: string;
}

// Complete order timeline response
export interface OrderTimeline {
  order_id: number;
  activities: OrderTimelineActivity[];
  total_count: number;
  grouped_by_date?: Record<string, OrderTimelineActivity[]>;
}

// Order history entry
export interface OrderHistoryEntry {
  id: string;
  field: string;
  old_value: any;
  new_value: any;
  changed_by: string;
  changed_at: string;
  change_type: 'create' | 'update' | 'delete';
  notes?: string;
}

// Complete order history response
export interface OrderHistory {
  order_id: number;
  entries: OrderHistoryEntry[];
  total_changes: number;
}

// Order filter types for improved filtering
export interface OrderFilter {
  status?: OrderStatus | OrderStatus[];
  customer_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  payment_status?: 'pending' | 'partial' | 'paid';
  has_tracking?: boolean;
  has_invoice?: boolean;
  quickbooks_synced?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// ============================================================================
// CRM INTERACTION HISTORY TYPES
// ============================================================================

// Contact interaction types
export interface ContactInteraction {
  id: string;
  type: 'order' | 'email' | 'phone' | 'meeting' | 'note' | 'submission' | 'status_change';
  title: string;
  description: string;
  timestamp: string;
  user?: string;
  metadata?: {
    order_id?: number;
    submission_id?: string;
    note_id?: number;
    old_status?: string;
    new_status?: string;
    [key: string]: any;
  };
  direction?: 'inbound' | 'outbound';
  channel?: string;
}

// Complete interaction history response
export interface ContactInteractionHistory {
  contact_id: number;
  interactions: ContactInteraction[];
  total_count: number;
  grouped_by_date?: Record<string, ContactInteraction[]>;
  summary?: {
    total_orders: number;
    total_emails: number;
    total_calls: number;
    total_meetings: number;
    total_notes: number;
    last_interaction: string;
  };
}

// Contact notification preferences
export interface ContactNotificationPreferences {
  id: number;
  contact_id: number;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  marketing_emails: boolean;
  transactional_emails: boolean;
  order_updates: boolean;
  newsletter: boolean;
  frequency?: 'immediate' | 'daily' | 'weekly' | 'monthly';
  preferred_time?: string;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  created_at?: string;
  updated_at?: string;
}

// ============================================================================
// ADVANCED INVENTORY TYPES
// ============================================================================

// Product recommendation types
export interface ProductRecommendation {
  product: InventoryItem;
  score: number;
  reason: string;
  recommendation_type: 'similar' | 'frequently_bought_together' | 'trending' | 'personalized';
  metadata?: {
    similarity_score?: number;
    purchase_count?: number;
    trend_score?: number;
    [key: string]: any;
  };
}

// Batch activity types
export interface BatchActivity {
  id: string;
  timestamp: string;
  action: string;
  actor: string;
  batch_id?: string;
  product_id?: number;
  product_name?: string;
  quantity?: number;
  metadata?: Record<string, any>;
  description: string;
}

// Production dashboard types
export interface ProductionDashboard {
  total_runs: number;
  status_breakdown: {
    pending: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
  completion_rate: number;
  efficiency_score: number;
  average_duration: {
    hours: number;
    formatted: string;
  };
  trending: {
    period: string;
    runs_count: number[];
    dates: string[];
  };
  top_products: Array<{
    product_id: number;
    product_name: string;
    runs_count: number;
    total_quantity: number;
  }>;
  recent_runs: Array<{
    id: string;
    product_name: string;
    status: string;
    started_at: string;
    completed_at?: string;
    quantity: number;
  }>;
}

// Old MaterialTransfer and MaterialTransferTracking removed - see new definitions below (line ~1566)

// ============================================================================
// ADDITIONAL TYPE DEFINITIONS
// ============================================================================

// Contact submission status type
export type ContactSubmissionStatus = 'todo' | 'doing' | 'done' | 'archived';

// React DnD drag item type for Kanban
export interface DragItem {
  type: string;
  submission: ContactSubmission;
  id: string;
}

// ============================================================================
// ACTIVITY SYSTEM TYPES
// ============================================================================

// Individual activity item from the unified activity feed
export interface ActivityItem {
  timestamp: string;
  activity_type: string;
  description: string;
  user: string;
  metadata: {
    text: string;
    user: string;
    activity: string;
    timestamp: string;
    [key: string]: any;
  };
}

// Contact-specific activities response
export interface ContactActivityResponse {
  contact_id: number;
  contact_name: string;
  total_activities: number;
  activities: ActivityItem[];
  filters_applied: {
    activity_type: string | null;
    start_date: string | null;
    end_date: string | null;
    limit: number;
  };
}

// Order-specific activities response  
export interface OrderActivityResponse {
  order_id: number;
  order_number: string;
  customer: string;
  total_activities: number;
  activities: ActivityItem[];
  filters_applied: {
    activity_type: string | null;
    start_date: string | null;
    end_date: string | null;
    limit: number;
  };
}

// Unified activity feed item (includes source information)
export interface UnifiedActivityItem extends ActivityItem {
  source: 'contact' | 'order';
  source_id: number;
  source_name: string;
}

// Unified activity feed response
export interface UnifiedActivityFeedResponse {
  total_activities: number;
  activities: UnifiedActivityItem[];
  available_filters: {
    activity_types: string[];
    users: string[];
    sources: ('contact' | 'order' | 'all')[];
  };
  filters_applied: {
    source: 'contact' | 'order' | 'all';
    activity_type: string | null;
    user: string | null;
    start_date: string | null;
    end_date: string | null;
    limit: number;
  };
}

// Activity filter parameters for API requests
export interface ActivityFilterParams {
  activity_type?: string;
  start_date?: string;  // YYYY-MM-DD format
  end_date?: string;    // YYYY-MM-DD format  
  limit?: number;
  source?: 'contact' | 'order' | 'all'; // Only for unified feed
  user?: string;        // Only for unified feed
}

// Activity type definitions based on API documentation
export type ContactActivityType = 
  | 'contact_created'
  | 'contact_updated'
  | 'contact_note_added'
  | 'contact_submission_created'
  | 'contact_submission_status_changed';

export type OrderActivityType =
  | 'order_created'
  | 'order_status_changed'
  | 'order_note_added'
  | 'order_payment_received'
  | 'order_shipped';

export type ActivityType = ContactActivityType | OrderActivityType;

// Activity polling/real-time update configurations
export interface ActivityPollingConfig {
  interval: number;           // Polling interval in milliseconds
  maxInterval: number;        // Maximum polling interval
  enabled: boolean;
  lastActivityTimestamp?: string;
}

// UI-specific activity display item (extends API response with UI state)
export interface ActivityDisplayItem extends UnifiedActivityItem {
  id: string;              // Computed unique ID for React keys
  relativeTime: string;    // Formatted relative time string
  icon: string;           // Icon identifier for the activity type
  badgeColor: string;     // CSS color class for type badge
  isExpanded?: boolean;   // UI expansion state
}

// ============================================================================
// HARVEST OPERATIONS TYPES
// ============================================================================

// Driver info for material transfers
export interface DriverInfo {
  name: string;
  contact: string;
  vehicle?: string;
  license_plate?: string;
}

// Harvest plan metadata type
export interface HarvestPlanMetadata {
  notes?: string;
  planting_date?: string;
  strain_notes?: string;
  crew_size?: number;
  actual_crew_size?: number;
  grow_site_name?: string;     // Grow site for ODA lot ID generation
  grow_sites?: string[];        // Multiple grow sites
  default_storage_receiver?: {  // For material transfers
    business_name?: string;
    oda_license?: string;
    address?: string;
    contact_name?: string;
    contact_phone?: string;
  };
  drivers?: DriverInfo[];       // Saved drivers for material transfers
  [key: string]: any;          // Flexible JSONB field
}

// Harvest plan management
export interface HarvestPlan {
  id: number;
  name: string;                    // Required: "Fall 2025 Harvest - Cherry Wine"
  farm: number;                    // Required: Farm ID (foreign key)
  farm_name?: string;              // Farm name (e.g., "Demo North Field")
  planned_harvest_date?: string;   // ISO date
  actual_harvest_date?: string;    // ISO date
  projected_yield?: string;        // Decimal string (e.g. "5000.00")
  pre_harvest_coa_date?: string;   // COA must be within 28 days of harvest
  pre_harvest_coa_file?: string;   // File path/URL
  metadata: HarvestPlanMetadata;
  created_at: string;
  updated_at: string;
}

// Container metadata type
export interface ContainerMetadata {
  lot_id?: string;           // ODA lot ID (e.g., "NorthField-2025-001")
  grow_site_name?: string;   // Grow site name for lot ID generation
  harvest_date?: string;     // ISO date
  quality_notes?: string;    // Quality assessment notes
  moisture_content?: string; // Moisture percentage
  trim_notes?: string;       // Trimming notes
  [key: string]: any;       // Flexible JSONB field
}

// Hemp record (container) with operational context
export interface Container {
  id: number;
  barcode: string;              // Auto-generated (e.g., "01000000123")
  strain: number;               // Strain ID (foreign key)
  strain_name: string;          // Strain name (e.g., "Cherry Wine CBD")
  strain_terpene: string;       // Strain type (Indica/Sativa/Hybrid)
  weight: string;               // Original container weight (can be "0")
  farm: number;                 // Farm ID
  farm_name: string;            // Farm name (e.g., "Demo North Field")

  // Operational context - exactly one of these is set:
  harvest_plan?: number;        // Harvest context
  tolling_contract?: number;    // Tolling context

  // Test results
  heavy_metals: number;         // 0=Pass, 1=Fail, 2=Not Tested
  pesticides: number;           // 0=Pass, 1=Fail, 2=Not Tested
  cbd?: number;                 // CBD percentage
  thc?: number;                 // THC percentage
  other_issues?: string;        // Other test issues
  notes?: string;               // General notes

  metadata: ContainerMetadata;

  created_at: string;
  updated_at: string;

  // Computed fields (from backend)
  operational_context: 'harvest' | 'tolling' | 'rnd' | 'legacy';
  total_pallet_weight: string;  // Sum of all pallet net weights
  effective_weight: string;     // Real weight (pallet weight if pallets exist, otherwise container weight)
  pallet_count?: number;        // Count of pallets in this container
}

// Pallet metadata type
export interface PalletMetadata {
  packaged_date: string;      // Duplicate for JSONB queries
  status: string;            // Status in metadata for filtering
  processing_notes?: string;
  last_scanned?: string;     // ISO datetime
  qr_data: {
    pallet_id: string;
    container_id: number;
    lot_id: string;
    weight: number;
    harvest_date: string;
    location: string;
  };
  [key: string]: any;        // Flexible JSONB field
}

// Pallet with QR code and weight tracking
export interface Pallet {
  id: number;
  barcode: string;              // Auto-generated (e.g., "12000000001")
  container: number;            // Container ID (foreign key)
  location?: number;            // Location ID (foreign key, optional)

  // Weight tracking
  gross_weight: string;         // Decimal string (e.g., "525.50")
  tare_weight: string;          // Decimal string (e.g., "25.50", default: "0.00")
  net_weight: string;           // Auto-calculated: gross - tare

  // Status and processing
  status: 'created' | 'labeled' | 'stored' | 'in_transit' | 'processing' | 'consumed';
  packaged_date: string;        // ISO date (required)
  processing_notes?: string;    // Optional notes

  metadata: PalletMetadata;

  created_at: string;
  updated_at: string;

  // Computed fields (from backend)
  qr_code: string;             // Base64-encoded PNG image
  last_scanned?: string;       // From metadata.last_scanned
  qr_data: object;            // QR code embedded data
}

// Material transfer for pallet movements
// Old MaterialTransfer removed - see imports from @/lib/schemas/transferRequest below

// Create/Update input types
export interface HarvestPlanCreateInput {
  name: string;
  farm: number;
  planned_harvest_date?: string;
  projected_yield?: number;
  metadata?: Record<string, any>;
}

export interface HarvestPlanUpdateInput {
  name?: string;
  planned_harvest_date?: string | null;
  actual_harvest_date?: string | null;
  projected_yield?: number | null;
  pre_harvest_coa_date?: string | null;
  metadata?: Record<string, any>;
}

export interface ContainerCreateInput {
  // === REQUIRED ===
  strain_name: string;           // Creates strain if doesn't exist
  weight: number;                // Weight in pounds
  farm: number;                  // Farm ID
  heavy_metals: number;          // 0=Pass, 1=Fail, 2=Not Tested
  pesticides: number;            // 0=Pass, 1=Fail, 2=Not Tested

  // === OPTIONAL ===
  harvest_plan?: number;         // Harvest plan ID
  location?: number;             // Storage location ID
  cbd?: number;                  // CBD percentage
  thc?: number;                  // THC percentage
  container_type?: string;       // "box", "bag", etc.
  other_issues?: string;         // Other test issues
  notes?: string;                // General notes
  unit_price?: number;           // Price per pound
  document?: File;               // Primary COA document
  document2?: File;              // Secondary COA document
  metadata?: {                   // Harvest/tracking data
    lot_id?: string;
    harvest_date?: string;
    quality_notes?: string;
    moisture_content?: string;
    trim_notes?: string;
    [key: string]: any;
  };
}

export interface PalletCreateInput {
  container: number;
  location?: number;
  gross_weight: number;
  tare_weight?: number;
  packaged_date: string;
  processing_notes?: string;
  status?: 'created' | 'labeled' | 'stored' | 'in_transit' | 'processing' | 'consumed';
}

export interface PalletUpdateInput {
  gross_weight?: number;
  tare_weight?: number;
  packaged_date?: string;
  location?: number;
  status?: 'created' | 'labeled' | 'stored' | 'in_transit' | 'processing' | 'consumed';
  processing_notes?: string;
}

// Old MaterialTransferCreateInput removed - see CreateTransferInput from schemas

// QR Code response type
export interface PalletQRCodeResponse {
  qr_code: string;  // Base64-encoded PNG image
  data: {
    pallet_id: string;
    container_id: number;
    lot_id: string;
    weight: number;
    harvest_date: string;
    location: string;
  };
}

// QR scan request type
export interface PalletScanRequest {
  qr_data: string;    // JSON string from QR code
  location_id?: number; // Optional location update
}

// Label generation types
export interface BulkLabelRequest {
  pallet_ids: number[];
  format?: 'pdf' | 'html';
}

// Harvest locations interface
export interface Location {
  id: number;
  name: string;
  description?: string;
}

// Paginated responses for harvest operations
export interface PaginatedHarvestPlans extends PaginatedResponse<HarvestPlan> {}
export interface PaginatedContainers extends PaginatedResponse<Container> {}
export interface PaginatedPallets extends PaginatedResponse<Pallet> {}

// ============================================================================
// MATERIAL TRANSFER TYPES - imported from Zod schemas (single source of truth)
// ============================================================================

// Import Material Transfer types from Zod schemas (single source of truth)
// NOTE: Pallet and Container already defined above, not re-imported
export type {
  TransferType,
  TransferStatus,
  MetadataType,
  BusinessInfo,
  DriverInfo,
  MaterialTransfer,
  MaterialTransferItem,
  TransferNote,
  CreateTransferInput,
  UpdateTransferInput,
  AddPalletInput,
  AddNoteInput,
  GeneratePDFResponse,
  TransferStats,
  AvailablePalletsResponse,
  PaginatedTransfers,
} from '@/lib/schemas/transferRequest'

// ============================================================================
// EXTRACTION TYPES
// ============================================================================

export interface ExtractionMachine {
  id: number;
  uuid: string;
  name: string;
  color: string;  // 6-character hex code
  ts: ISODateTime;
}

export interface ExtractionMachineCreateInput {
  name: string;
  color: string;
}

export interface ScheduledStrain {
  id: number;
  uuid: string;
  date: string;  // YYYY-MM-DD
  strain: {
    id: number;
    barcode: string;
    strain_name: string;
    farm_name: string;
    weight: number;
  };
  quantity: number;
  machine: ExtractionMachine;
  ts: ISODateTime;
}

export interface ScheduledStrainCreateInput {
  date: string;
  strain_id: number;
  quantity: number;
  machine_id: number;
}

export interface ScheduledStrainUpdateInput {
  date?: string;
  strain_id?: number;
  quantity?: number;
  machine_id?: number;
}

export interface BulkMoveInput {
  ids: number[];
  new_date: string;
}

export interface BatchBulkScanInput {
  barcodes: string[];
  batch_id?: number;
}

export interface BatchMinimal {
  id: number;
  barcode: string;
  status: string;
  input_weight: number;
  output_weight: number;
  strain_name: string;
  farm_name: string;
  sock_count: number;
  yield_percentage: number;
  verified: boolean;
  can_postpurge: boolean;
  ts: ISODateTime;
}

export interface PreBatchMinimal {
  id: number;
  barcode: string;
  weight: number;
  container_barcode: string;
  strain_name: string;
  farm_name: string;
}

export interface BulkScanResponse {
  scanned_count: number;
  found_count: number;
  not_found: string[];
  already_assigned: Array<{ barcode: string; current_batch_id: number }>;
  socks: PreBatchMinimal[];
  batch?: BatchMinimal;
}

export interface ExtractionKanbanResponse {
  columns: Record<string, BatchMinimal[]>;
  status_labels: Record<string, string>;
}

export interface ExtractionDashboard {
  batches_by_status: Record<string, number>;
  pending_qa_count: number;
  verified_today: number;
  total_input_weight: number;
  total_output_weight: number;
  average_yield: number;
  machines: ExtractionMachine[];
  today_schedules_count: number;
}

export type PaginatedScheduledStrains = PaginatedResponse<ScheduledStrain>;
export type PaginatedExtractionMachines = PaginatedResponse<ExtractionMachine>;

// ============================================================================
// PROCESSING TYPES
// ============================================================================

export type ProcessingStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type OilType = 'CONCENTRATE' | 'TERPENE_EXTRACT' | 'THC_COMPLIANT' | 'HCE';

export interface VendorMinimal {
  id: number;
  name: string;
}

export interface StorageVesselMinimal {
  id: number;
  name: string;
  tare_weight: number;
}

export interface LocationMinimal {
  id: number;
  name: string;
  barcode: string;
}

export interface BatchProduct {
  id: number;
  barcode: string;
  weight: number;
  tare_weight: number;
  net_weight: number;
  available_weight: number;
  oil_type: OilType;
  strain_name: string;
  farm_name: string;
  vessel_name: string;
  archive: boolean;
  ts: ISODateTime;
}

export interface BatchProductProcessing {
  id: number;
  created_ts: ISODateTime;
  updated_ts: ISODateTime;
  status: ProcessingStatus;
  vendor: VendorMinimal;
  inputs: BatchProduct[];
  outputs: BatchProduct[];
  total_input_weight: number;
  total_output_weight: number;
  yield_percentage: number;
}

export interface BatchProductProcessingCreateInput {
  vendor_id: number;
}

export interface AssignInputsInput {
  product_ids: number[];
}

export interface IngestOutputItem {
  weight: string;
  tare_weight?: string;
  oil_type: 'TERPENE_EXTRACT' | 'HCE';
  vessel_id?: number;
  location_id?: number;
  container_id?: number;
  notes?: Record<string, any>;
}

export interface IngestOutputsInput {
  outputs: IngestOutputItem[];
}

export interface ProcessingDashboard {
  processing_by_status: Record<string, number>;
  vendor_stats: Array<{
    id: number;
    name: string;
    processing_count: number;
    completed_count: number;
    average_yield: number;
  }>;
  total_input_weight: number;
  total_output_weight: number;
  average_yield: number;
  pending_count: number;
  in_progress_count: number;
  completed_today: number;
}

export interface AvailableProductsResponse {
  count: number;
  products: BatchProduct[];
}

export interface TraceabilityLineage {
  type: string;
  id: number;
  barcode?: string;
  description: string;
  weight?: number;
  input_weight?: number;
  output_weight?: number;
  status?: string;
  timestamp: ISODateTime;
}

export interface TraceabilityResponse {
  batch_product: BatchProduct;
  container: {
    id: number;
    barcode: string;
    weight: number;
    farm_name: string | null;
    strain_name: string | null;
    ts: ISODateTime;
  } | null;
  batches: Array<{
    id: number;
    barcode: string;
    input_weight: number;
    output_weight: number;
    status: string;
    strain_name: string | null;
    farm_name: string | null;
    ts: ISODateTime;
  }>;
  processing: BatchProductProcessing | null;
  lineage: TraceabilityLineage[];
}

export type PaginatedBatchProductProcessings = PaginatedResponse<BatchProductProcessing>;

// ============================================================================
// TOLLING CONTRACTS
// ============================================================================

export type TollingContractStatus = 'draft' | 'active' | 'completed' | 'cancelled';

export interface TollingContractCustomer {
  id: number;
  name: string;
  email?: string;
}

export interface TollingContract {
  uuid: UUID;
  id: number;
  // Business (customer) fields from backend
  business_id: number | null;
  business_name: string | null;
  // Contract type fields from backend
  contract_type_id: number | null;
  contract_type_name: string | null;
  status: TollingContractStatus;
  start_date: ISODateTime | null;
  end_date: ISODateTime | null;
  // weight = contracted input weight (lbs); term_weight = per-term weight
  weight: DecimalString | null;
  term_weight: DecimalString | null;
  term: string | null;
  unit_price: DecimalString | null;
  container_count: number;
  notes: string | null;
  // Backend uses ts/updated_ts
  ts: ISODateTime;
  updated_ts: ISODateTime;
}

export interface TollingContractDetail extends TollingContract {
  business: { id: number; name: string } | null;
  contract_type: { id: number; name: string; config: Record<string, unknown> } | null;
}

export interface TollingContractCreateInput {
  business_id?: number | null;
  contract_type_id?: number | null;
  status?: TollingContractStatus;
  start_date?: string | null;
  end_date?: string | null;
  weight?: string | null;
  term_weight?: string | null;
  term?: string | null;
  unit_price?: string | null;
  notes?: string | null;
}

export interface TollingContractUpdateInput extends TollingContractCreateInput {}

export interface TollingContractProgress {
  uuid: UUID;
  total_input_weight: DecimalString;
  total_assigned_weight: DecimalString;
  total_output_weight: DecimalString;
  percent_complete: number;
  yield_percent: number;
  container_count: number;
  sock_count: number;
  batch_count: number;
  jar_count: number;
}

export interface TollingContainerAssignment {
  id: number;
  uuid: UUID;
  barcode: string;
  weight: number | null;
  farm_name: string | null;
  strain_name: string | null;
  sock_count: number;
  assigned_at: ISODateTime;
}

export interface TollingAssignContainersInput {
  container_ids: number[];
}

export interface TollingContractSock {
  id: number;
  uuid: UUID;
  barcode: string;
  weight: DecimalString | null;
  farm_name: string | null;
  strain_name: string | null;
  container_id: number | null;
  ts: ISODateTime;
  created: ISODateTime;
}

export interface TollingContractBatch {
  uuid: UUID;
  id: number;
  barcode: string;
  status: string;
  input_weight: DecimalString | null;
  output_weight: DecimalString | null;
  farm_name: string | null;
  strain_name: string | null;
  is_tolling: boolean;
  ts: ISODateTime;
}

export interface TollingContractJar {
  id: number;
  uuid: UUID;
  barcode: string;
  weight: DecimalString | null;
  tare_weight: DecimalString | null;
  net_weight: DecimalString | null;
  oil_type: string;
  farm_name: string | null;
  strain_name: string | null;
  ts: ISODateTime;
  created: ISODateTime;
}

export type PaginatedTollingContracts = PaginatedResponse<TollingContract>;
export type PaginatedTollingContainerAssignments = PaginatedResponse<TollingContainerAssignment>;
export type PaginatedTollingContractSocks = PaginatedResponse<TollingContractSock>;
export type PaginatedTollingContractBatches = PaginatedResponse<TollingContractBatch>;
export type PaginatedTollingContractJars = PaginatedResponse<TollingContractJar>;

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// ============================================================================

// Re-export types with alternative names for backward compatibility
export type { Contact as CustomerContact };
export type { Order as CustomerOrder };
export type { OrderItem as CustomerOrderItem };
export type { InventoryItem as Product };
export type { ContactSubmission as ContactSubmissionResponse };
export type { DashboardStats as AdminDashboardStats };